import type { SortOrder } from 'antd/es/table/interface';

import { Optional } from 'chaika-common-types';

import { isDefined } from '@utils/isDefined';

import type { MadeColumnType } from '../hooks/useMakeColumns';
import type { LocalSortState } from '../VirtualTable/useLocalSort';

export const QUERY_URL_KEY = 'query';
export const PRESET_URL_KEY = 'preset';
export const SORT_URL_KEY = 'sort';

const SORT_ORDERS: SortOrder[] = ['ascend', 'descend'];

export function buildUrlKey(urlKey: string, name: string): string {
    return `${urlKey}.${name}`;
}

export function findTableKeys(urlSearchParams: URLSearchParams, urlKey: string): string[] {
    const prefix = `${urlKey}.`;

    return Array.from(urlSearchParams.keys()).filter(key => key.startsWith(prefix));
}

export function collectFilterableColumnKeys(columns: MadeColumnType[]): Set<string> {
    return new Set(
        columns.filter(column => !!column.filterDropdown && isDefined(column.key)).map(column => String(column.key)),
    );
}

export function readPresetIndex(urlSearchParams: URLSearchParams, urlKey: string): Optional<number> {
    const rawPresetIndex = urlSearchParams.get(buildUrlKey(urlKey, PRESET_URL_KEY));

    if (!rawPresetIndex) {
        return undefined;
    }

    const presetIndex = Number(rawPresetIndex);

    return Number.isInteger(presetIndex) && presetIndex >= 0 ? presetIndex : undefined;
}

export function readSort(
    urlSearchParams: URLSearchParams,
    urlKey: string,
    columns: MadeColumnType[],
): Optional<LocalSortState> {
    const rawSort = urlSearchParams.get(buildUrlKey(urlKey, SORT_URL_KEY));

    if (!rawSort) {
        return undefined;
    }

    const separatorIndex = rawSort.lastIndexOf(':');
    const columnKey = rawSort.slice(0, separatorIndex);
    const order = rawSort.slice(separatorIndex + 1) as SortOrder;

    if (!SORT_ORDERS.includes(order) || !isSortableColumn(columns, columnKey)) {
        return undefined;
    }

    return { columnKey, order };
}

function isSortableColumn(columns: MadeColumnType[], columnKey: string): boolean {
    return columns.some(column => String(column.key) === columnKey && !!column.sorter);
}

export function serializeFilterValue(value: unknown): string {
    if (Array.isArray(value)) {
        return value.filter(item => item !== null && item !== undefined && item !== '').join(',');
    }

    if (value === null || value === undefined || value === false || value === '') {
        return '';
    }

    return String(value);
}
